import { useMemo } from "react";
import { parseLinks } from "@astrum/shared";
import "./linked-text.css";

/**
 * Plain text with any URLs in it rendered as clickable links.
 *
 * For text that has no mentions to layer in — Feed posts and comments, unlike
 * chat messages, never reference a person by id. Chat's own MessageBody does
 * the same link-finding inline, since there mentions and links have to be
 * merged into one ordered list of spans rather than found independently.
 */
export function LinkedText({ text }: { text: string }) {
  const parts = useMemo(() => {
    const links = parseLinks(text);
    if (links.length === 0) return null;

    const out: { text: string; url?: string }[] = [];
    let cursor = 0;
    for (const link of links) {
      if (link.start > cursor) out.push({ text: text.slice(cursor, link.start) });
      out.push({ text: link.url, url: link.url });
      cursor = link.end;
    }
    if (cursor < text.length) out.push({ text: text.slice(cursor) });
    return out;
  }, [text]);

  if (!parts) return <>{text}</>;

  return (
    <>
      {parts.map((part, index) =>
        part.url ? (
          <a key={index} href={part.url} target="_blank" rel="noopener noreferrer" className="linked-text__link">
            {part.text}
          </a>
        ) : (
          <span key={index}>{part.text}</span>
        ),
      )}
    </>
  );
}
